import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, Code, Sparkle } from "@phosphor-icons/react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate("/", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail?.verification_required) {
        toast.error("Please verify your email before logging in.");
        const params = new URLSearchParams();
        params.set("email", detail.email || email);
        navigate(`/verify-email?${params.toString()}`);
        return;
      }
      const message = typeof detail === "string" ? detail : detail?.message;
      toast.error(message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771875797242-77f69f061132?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxuZW9uJTIwdGVjaCUyMGFic3RyYWN0fGVufDB8fHx8MTc3NDM3OTM5OHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <Code weight="bold" className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">DIIT Showcase</h1>
          </div>
          <h2 className="font-heading text-4xl font-bold tracking-tight mb-4 leading-tight">
            Show what you built.<br />
            <span className="text-primary neon-text">Get inspired.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
            Share your projects, get feedback from peers, and build your portfolio alongside fellow students.
          </p>
          <div className="mt-6">
            <div className="flex items-center gap-3">
              <Button asChild variant="outline" className="rounded-full border-accent/50 text-accent hover:bg-accent/10">
                <Link to="/feed/public" data-testid="hero-public-feed-link">
                  Browse Public Feed
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-accent/50 text-accent hover:bg-accent/10">
                <Link to="/leaderboard/public" data-testid="hero-public-leaderboard-link">
                  Explore Public Leaderboard
                </Link>
              </Button>
            </div>
          </div>
          <div className="mt-12 flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkle weight="fill" className="w-4 h-4 text-accent" />
            <span>200+ students already showcasing their work</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/50 flex items-center justify-center">
              <Code weight="bold" className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-xl font-bold">DIIT Showcase</h1>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">Welcome back</h2>
            <p className="text-muted-foreground">Sign in to continue to your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@diit.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-surface border-border focus:border-primary transition-colors"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-surface border-border focus:border-primary transition-colors pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-full btn-neon"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              Admissions are invite-only. Take admission in DIIT Computers to receive your login details from the institute admin.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Parent or visitor?{" "}
              <Link
                to="/feed/public"
                className="text-accent hover:text-accent/80 font-medium transition-colors"
                data-testid="public-leaderboard-link"
              >
                View public feed
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
